import { createFileRoute, Link } from "@tanstack/react-router";
import { Mail, AlertCircle, Building2, Clock, ShieldCheck } from "lucide-react";
import logoAsset from "@/assets/lifewestway-logo.png.asset.json";
import { makeSeoDescription } from "@/lib/seo-description";
import { organizationSchema } from "@/lib/org-schema";

const SITE = "https://lifewestway.com";
const TITLE = "Contact Us | LifeWestWay";
const DESC =
  "Contact LifeWestWay for general inquiries, content corrections, or business partnerships. We respond within 1–3 business days. See important notes before reaching out.";
const URL_ = `${SITE}/contact`;
const IMAGE = `${SITE}${logoAsset.url}`;

const GOV_BODIES = [
  { country: "USA", body: "USCIS" },
  { country: "UK", body: "UKVI" },
  { country: "Australia", body: "Department of Home Affairs" },
  { country: "Italy", body: "Ministero degli Affari Esteri" },
  { country: "Portugal", body: "AIMA" },
  { country: "Canada", body: "IRCC" },
];

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: makeSeoDescription(DESC) },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: makeSeoDescription(DESC) },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_ },
      { property: "og:image", content: IMAGE },
      { property: "og:site_name", content: "LifeWestWay" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: makeSeoDescription(DESC) },
      { name: "twitter:image", content: IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://lifewestway.com/contact" }],
  }),
  component: ContactPage,
});

function ContactPage() {
  const contactJsonLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Contact LifeWestWay",
    url: URL_,
    description: DESC,
    mainEntity: organizationSchema(),
  };


  return (
    <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(contactJsonLd) }}
      />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link title="Return to the LifeWestWay homepage" to="/" className="hover:text-accent">
          Home
        </Link>
        <span className="px-2">/</span>
        <span className="text-foreground">Contact Us</span>
      </nav>

      {/* H1 Hero */}
      <header className="mt-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
          Get in Touch
        </p>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-foreground sm:text-5xl">
          Contact Us
        </h1>
        <p className="mt-5 max-w-3xl text-lg leading-relaxed text-foreground/75">
          We'd love to hear from you. Whether you have a question about one of our guides, spotted
          an error, have a topic suggestion, or want to discuss a partnership, here's how to reach
          us.
        </p>
      </header>

      {/* Contact channels */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">General Inquiries</h2>
        <div className="mt-4 rounded-2xl border border-border bg-card/60 p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-accent/30 bg-accent/10 text-accent">
              <Mail className="h-5 w-5" />
            </span>
            <div className="flex-1">
              <p className="text-sm leading-7 text-foreground/80">
                For general questions about our content, corrections, or feedback:
              </p>
              <a title="Email the LifeWestWay editorial team"
                href="mailto:info.lifewestway@gmail.com"
                className="mt-2 inline-block font-display text-lg font-bold text-foreground transition-colors hover:text-accent"
              >
                info.lifewestway@gmail.com
              </a>
              <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                <span>
                  We aim to respond to all messages within{" "}
                  <strong className="text-foreground">1–3 business days</strong>.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-8">
        <h2 className="font-display text-2xl font-bold text-accent">
          Content Corrections & Suggestions
        </h2>
        <div className="mt-4 rounded-2xl border border-border bg-card/60 p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-accent/30 bg-accent/10 text-accent">
              <AlertCircle className="h-5 w-5" />
            </span>
            <div className="flex-1">
              <p className="text-sm leading-7 text-foreground/80">
                Immigration rules, tax figures, and visa requirements change frequently. If you spot
                outdated information or have a source you'd like us to review, please let us know:
              </p>
              <a title="Email LifeWestWay about corrections"
                href="mailto:lifewestway@gmail.com"
                className="mt-2 inline-block font-display text-lg font-bold text-foreground transition-colors hover:text-accent"
              >
                lifewestway@gmail.com
              </a>
              <p className="mt-3 text-xs text-muted-foreground">
                Please include the article title or URL and a brief description of what you'd like
                us to check.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-8">
        <h2 className="font-display text-2xl font-bold text-accent">
          Business & Partnership Inquiries
        </h2>
        <div className="mt-4 rounded-2xl border border-border bg-card/60 p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-accent/30 bg-accent/10 text-accent">
              <Building2 className="h-5 w-5" />
            </span>
            <div className="flex-1">
              <p className="text-sm leading-7 text-foreground/80">
                For advertising, partnership, or collaboration inquiries:
              </p>
              <a title="Email LifeWestWay about partnerships"
                href="mailto:lifewestway@gmail.com"
                className="mt-2 inline-block font-display text-lg font-bold text-foreground transition-colors hover:text-accent"
              >
                lifewestway@gmail.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Before you reach out */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">Before You Reach Out</h2>
        <p className="mt-4 text-sm leading-7 text-foreground/80">
          For the fastest answers, please note:
        </p>
        <div className="mt-5 rounded-2xl border border-accent/25 bg-card/60 p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-accent/30 bg-accent/10 text-accent">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <div className="space-y-4 text-sm leading-7 text-foreground/80">
              <p>
                <strong className="font-semibold text-foreground">
                  We are not a law firm, immigration consultancy, or financial advisory service.
                </strong>{" "}
                We cannot provide personalized legal, immigration, or tax advice, and we're not able
                to review or comment on individual visa applications, case statuses, or personal
                financial situations.
              </p>
              <p>
                For case-specific questions, please contact a licensed immigration attorney,
                registered migration agent, or qualified accountant/financial advisor in your
                target country.
              </p>
              <p>
                For official, authoritative information, always refer to the relevant government
                agency:
              </p>
            </div>
          </div>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {GOV_BODIES.map((g) => (
              <div
                key={g.country}
                className="rounded-lg border border-border bg-navy/20 px-4 py-3"
              >
                <h3 className="font-display text-sm font-semibold text-foreground">
                  {g.country}
                </h3>
                <span className="mt-1 block text-xs text-muted-foreground">{g.body}</span>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* What happens after you write */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">What Happens After You Write</h2>
        <div className="mt-4 space-y-4 text-sm leading-7 text-foreground/80">
          <p>
            Every message that arrives is read by a member of the LifeWestWay editorial team, not an
            automated system. Corrections are handled first, because an out-of-date fee, threshold or
            processing time in one of our guides can cost a reader real money. If you tell us that a
            figure looks wrong, we go back to the primary source — USCIS and the IRS for the United
            States, the Home Office and HMRC for the United Kingdom, the Department of Home Affairs
            and the ATO for Australia, IRCC and the CRA for Canada, the Questura and Agenzia delle
            Entrate for Italy, and AIMA and the Autoridade Tributária for Portugal — confirm the
            current position, and update the page. Where the change is material, we note it in the
            article so returning readers can see what moved.
          </p>
          <p>
            Topic suggestions are grouped and reviewed together. We are more likely to write a guide
            when several readers describe the same gap, when the question has a documented answer in
            official sources, and when the topic is not already better covered inside an existing
            guide. If your suggestion fits an article we already publish, we will usually expand that
            page rather than create a thin new one — a deeper guide is more useful than two shallow
            ones, and it is easier for us to keep accurate over time.
          </p>
          <p>
            Personal relocation stories are welcome, and they genuinely shape what we publish. Details
            such as how long an appointment backlog actually ran at a particular consulate, what a
            landlord asked for beyond the legal deposit, or which document a bank refused to accept
            are the kind of practical friction that rarely appears in official guidance. We never
            publish your name, email address or identifying details from a message without explicit
            written permission, and where an experience informs a guide we describe it in general
            terms as part of a composite example.
          </p>
          <p>
            What we cannot do is assess your individual case. We are unable to tell you whether you
            will qualify for a specific visa, review documents before submission, estimate your
            personal tax liability, or comment on the status of an application that is already with an
            authority. Those questions depend on facts we cannot verify and, in most countries, may
            only be answered by a licensed professional. Asking us will simply cost you time, so for
            anything case-specific please go directly to a regulated immigration lawyer, a registered
            migration agent, or a qualified accountant in your destination country.
          </p>
          <p>
            Response times vary with volume. Straightforward corrections are usually acknowledged
            within a few working days; longer editorial questions can take a week or more, especially
            where we need to verify something against a government source before replying. If your
            message is time-critical because of an application deadline, an official helpline will
            always be faster and more authoritative than we can be.
          </p>
        </div>
      </section>

      {/* Privacy consent note */}
      <section className="mt-12 rounded-2xl border border-border bg-navy/20 p-5 sm:p-6">
        <p className="text-sm leading-6 text-foreground/75">
          By contacting us, you agree that any information you share may be used to respond to your
          inquiry. See our{" "}
          <Link title="Read how LifeWestWay handles your data" to="/privacy" className="font-semibold text-accent hover:underline">
            Privacy Policy
          </Link>{" "}
          for details on how we handle your information.
        </p>
      </section>

      {/* Cross-links */}
      <section className="mt-10 flex flex-wrap gap-3">
        <Link title="Learn about LifeWestWay and its editorial team"
          to="/about"
          className="inline-flex items-center rounded-md border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-accent hover:text-accent"
        >
          About Us
        </Link>
        <Link title="Read the LifeWestWay editorial process"
          to="/editorial-process"
          className="inline-flex items-center rounded-md border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-accent hover:text-accent"
        >
          Editorial Process
        </Link>
        <Link title="Read the LifeWestWay terms of use"
          to="/terms"
          className="inline-flex items-center rounded-md border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-accent hover:text-accent"
        >
          Terms & Disclaimer
        </Link>
      </section>
    </div>
  );
}
